import { useState, useEffect } from "react";
import { FaThumbsDown, FaThumbsUp } from "react-icons/fa";
import styled from "styled-components";
import { CancelButton, TryAgainButton, ProgressBar, ProgressBarIndicator, MainText, ComplementaryText } from "./styles";
import { loadBase64Preview } from "~/utils/file";
import  DarkSpinner  from "~/shared/Spinner";
import theme from "~/styles/theme"

const imageExts = [".jpg", "jpeg", ".png"];

function humanFileSize(size: number) {
    const units = ["B", "kB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(size) / Math.log(1024));
    const formattedSize = size / Math.pow(1024, i);
    return `${formattedSize.toFixed(2)} ${units[i]}`;
}

function getOverlayColor(theme: Record<string, any>, status: string) {
    if (status === "uploaded") {
        return "#dcdcdc66";
    }

    return `${theme.color[status === "error" ? "danger" : "success"].background}`;
}

const Overlay = styled.div<{ status: string }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => getOverlayColor(theme, props.status)};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
`;

const FileContainer = styled.div<{ uploadPercent?: number }>`
    margin: 15px auto 5px auto;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    width: 100%;
    background: #dcdcdc;
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        position: absolute;
        top: 12px;
        left: 35px;
        color: ${theme.color.success.background};
    }

    ${ProgressBar} {
        position: absolute;
        bottom: 0;
    }
`;

const checkIfHasPreview = (fileName: string) => {
    return imageExts.some(ext => fileName?.toLowerCase()?.endsWith(ext));
};


const UploadItem = ({ fileDescriptor, onAbort, onRetry }) => {
    const { file, status, uploadPercent, message } = fileDescriptor

    const [hasPreview, setHasPreview] = useState(checkIfHasPreview(file?.name));
    const [base64Image, setBase64Image] = useState(null);

    useEffect(() => {
        setHasPreview(checkIfHasPreview(fileDescriptor?.file?.name));
    }, [fileDescriptor?.file?.name]);

    useEffect(() => {
        if (!checkIfHasPreview(fileDescriptor?.file?.name)) {
            return;
        }

        loadBase64Preview(fileDescriptor?.file?.src?.file)
            .then(url => setBase64Image(url))
            .catch(() => setHasPreview(false));
    }, [fileDescriptor?.file?.name, fileDescriptor?.file?.src?.file]);

    return (
        <>
            <FileContainer uploadPercent={uploadPercent}>
                {status !== "pending" ? (
                    <Overlay status={status}>
                        {status === "uploaded" ? (
                            <DarkSpinner />
                        ) : status === "success" ?  <FaThumbsUp /> : <FaThumbsDown />
                        }
                    </Overlay>
                ) : ("")}
                {hasPreview && base64Image ? <img alt="" src={base64Image} className="img-fluid" /> : <p>Sem preview</p>}
                {status === "pending" || status === "uploaded" ? (
                    <ProgressBar>
                        <ProgressBarIndicator style={{ width: `${uploadPercent}%` }} />
                    </ProgressBar>
                ) : (
                    ""
                )}
            </FileContainer>
            <MainText>{file?.name}</MainText>
            <div>
                <p>
                    {status === "pending" ? (
                        <>
                            <ComplementaryText>
                                {uploadPercent}% de {humanFileSize(file.size)}
                            </ComplementaryText>

                            <ComplementaryText style={{ marginLeft: "20px" }}>
                                <CancelButton type="button" onClick={() => onAbort(file.name)}>
                                    Cancelar
                                </CancelButton>
                            </ComplementaryText>
                        </>
                    ) : (
                        ""
                    )}

                    {status === "error" ? (
                        <>
                            <ComplementaryText>{message}</ComplementaryText>

                            <ComplementaryText style={{ marginLeft: "20px" }}>
                                <CancelButton type="button" onClick={() => onAbort(file.name)}>
                                    Cancelar
                                </CancelButton>
                            </ComplementaryText>

                            <ComplementaryText style={{ marginLeft: "20px" }}>
                                <TryAgainButton type="button" onClick={() => onRetry(file.name)}>
                                    Tentar novamente
                                </TryAgainButton>
                            </ComplementaryText>
                        </>
                    ) : (
                        ""
                    )}
                </p>
            </div>
        </>
    );
};

export default UploadItem;
